import React, { useState } from "react";

import { isMessageKey } from "common/i18n/types";

import { Layout } from "../components/layout/layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/auth-context";
import { useTranslations } from "../hooks/use-translations";

export const ChangePasswordPage = (): React.JSX.Element => {
  const [oldPassword, setOldPassword] = useState(``);
  const [newPassword, setNewPassword] = useState(``);
  const [confirmPassword, setConfirmPassword] = useState(``);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(``);
  const [success, setSuccess] = useState(``);

  const { changePassword } = useAuth();
  const { translations } = useTranslations();

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(``);
    setSuccess(``);

    if (newPassword !== confirmPassword) {
      setError(translations.common.passwordsDoNotMatch);
      setIsLoading(false);
      return;
    }

    try {
      await changePassword(oldPassword, newPassword);
      setSuccess(translations.changePassword.passwordChanged);
      setOldPassword(``);
      setNewPassword(``);
      setConfirmPassword(``);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : translations.changePassword.failure;

      if (errorMessage === `unexpectedError` || errorMessage.includes(`Internal server error`)) {
        setError(translations.common.serverError);
      } else if (errorMessage === `networkError` || errorMessage.includes(`Network error`)) {
        setError(translations.common.networkError);
      } else if (isMessageKey(errorMessage)) {
        setError(translations.auth[errorMessage]);
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="change-password">
        <Card>
          <CardHeader>
            <CardTitle>{translations.changePassword.title}</CardTitle>
            <CardDescription>{translations.changePassword.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleChangePassword}>
              {error ? (
                <div
                  className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-md"
                  data-testid="change-password-error"
                >
                  {error}
                </div>
              ) : null}

              {success ? (
                <div
                  className="text-green-600 dark:text-green-400 text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded-md"
                  data-testid="change-password-success"
                >
                  {success}
                </div>
              ) : null}

              <div>
                <Input
                  required
                  data-testid="old-password-input"
                  disabled={isLoading}
                  id="oldPassword"
                  name="oldPassword"
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder={translations.common.currentPasswordPlaceholder}
                  type="password"
                  value={oldPassword}
                />
              </div>

              <div>
                <Input
                  required
                  data-testid="new-password-input"
                  disabled={isLoading}
                  id="newPassword"
                  name="newPassword"
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={translations.common.newPasswordPlaceholder}
                  type="password"
                  value={newPassword}
                />
              </div>

              <div>
                <Input
                  required
                  data-testid="confirm-new-password-input"
                  disabled={isLoading}
                  id="confirmPassword"
                  name="confirmPassword"
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={translations.common.confirmNewPasswordPlaceholder}
                  type="password"
                  value={confirmPassword}
                />
              </div>

              <Button
                className="w-full"
                data-testid="change-password-button"
                disabled={isLoading}
                isLoading={isLoading}
                loadingText={translations.changePassword.changingPassword}
                type="submit"
              >
                {translations.changePassword.changePasswordButton}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
